import i18n from "@/i18n";
import SocketController from "@/services/Socket/socket.controller";
import {
  ResponseUsersAndCompanies,
  responseUsersAndCompaniesSchema,
} from "@/types";
import { validateWithSchema } from "@/utils/dataValidators.zod";

export async function fetchUsersWithCompanies(): Promise<
  | {
      rStatus: "success";
      rData: ResponseUsersAndCompanies;
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "getAllUsersWithCompanies",
      {},
      true
    );

    if (response.status === 200) {
      const validatedData = validateWithSchema<ResponseUsersAndCompanies>(
        response.data,
        responseUsersAndCompaniesSchema,
        "Users with Companies"
      );

      if (validatedData) {
        return {
          rStatus: "success",
          rData: validatedData,
        };
      } else {
        console.error("Invalid users data received", response.data);
        return {
          rStatus: "error",
          rData: i18n.t("Invalid users data received"),
        };
      }
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("No users found"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Internal server error. Please try again later"),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch users with companies"),
    };
  } catch (error) {
    console.error("Error fetching users with companies:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch users with companies"),
    };
  }
}

export async function sendCompanyMergeRequest(
  keepSideCompanyID: string,
  removeSideCompanyID: string
): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "adminCompaniesMerge",
      {
        keepId: keepSideCompanyID,
        removeId: removeSideCompanyID,
      },
      true
    );

    if (response.status === 200 || response.status === 202) {
      return {
        rStatus: "success",
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("Company not found. Please contact support"),
      };
    }

    if (response.status === 401 || response.status === 403) {
      return {
        rStatus: "error",
        rData: i18n.t(
          "Unauthorized or forbidden. Please check your permissions"
        ),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Internal server error. Please try again later"),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t(
        "Failed to merge companies. Please try again later or contact support"
      ),
    };
  } catch (error) {
    console.error("Error sending company merge request:", error);
    return {
      rStatus: "error",
      rData: i18n.t(
        "Failed to merge companies. Please try again later or contact support"
      ),
    };
  }
}
